export const highlightColor = {
    primary: '#ffffff',
    hover: '#e5e5e5',
    border: '#e5e5e5',
    gradient: {
        from: '#ffffff',
        to: '#999999',
        fromLight: '#ffffff',
        toGray: '#888888'
    }
};

/**
 * Centralized social media and contact links for CGM Sports
 */
export const contactEmail = 'contact@cgmsports.com';

export const socialLinks = {
    email: `mailto:${contactEmail}`,
    linkedin: 'https://www.linkedin.com/company/cgmsports',
    instagram: 'https://www.instagram.com/cgmsports.inc',
    tiktok: 'https://www.tiktok.com/@cgmsports.inc',
} as const;

export type SocialPlatform = keyof typeof socialLinks;

/**
 * Terms & Conditions content
 */
export const termsAndConditions = {
    title: "CGM Sports – Institutional Terms & Conditions",
    sections: [
        {
            heading: "1. Introduction",
            content: "These Terms & Conditions (\"Terms\") govern your access to and use of the CGM Sports platform (\"Services\"). By accessing or using the Services, you agree to be bound by these Terms."
        },
        {
            heading: "2. Scope of Services",
            content: "CGM Sports provides performance data aggregation and non-prescriptive analysis tools. The Services are intended for organizations such as sports teams, athletic departments, performance facilities, and PT clinics."
        },
        {
            heading: "3. User Eligibility",
            content: "You represent that you have the authority to bind the organization you act for. Individuals under institutional oversight (athletes, clients, staff) must have appropriate authorization."
        },
        {
            heading: "4. Account Responsibilities",
            content: "Users must provide accurate information and protect account credentials. Unauthorized sharing of access credentials is strictly prohibited."
        },
        {
            heading: "5. Data Ownership",
            content: "Organizations retain all rights to data uploaded, connected, or generated through the Services. CGM Sports processes data solely to provide the Services."
        },
        {
            heading: "6. Acceptable Use",
            content: "Users shall not engage in misuse, reverse engineering, data scraping, unauthorized access, or interference with platform functionality."
        },
        {
            heading: "7. Non-Prescriptive Nature of Insights",
            content: "CGM Sports provides informational insights derived from wearable and training data. These insights are not medical advice, clinical guidance, or training prescriptions and should not substitute coaching or professional judgment."
        },
        {
            heading: "8. Privacy and Compliance",
            content: "CGM Sports maintains administrative, technical, and physical safeguards supporting applicable educational and health privacy requirements, including FERPA and HIPAA, when relevant."
        },
        {
            heading: "9. Subscription and Payment",
            content: "Paid subscriptions renew automatically unless cancelled. Fees are non-refundable unless required by law."
        },
        {
            heading: "10. Platform Updates and Modifications",
            content: "CGM Sports may update, modify, or discontinue features of the Services. Material changes affecting paid users will be communicated in advance."
        },
        {
            heading: "11. Intellectual Property",
            content: "All CGM Sports software, designs, trademarks, and content remain the property of CGM Sports."
        },
        {
            heading: "12. Limitation of Liability",
            content: "To the maximum extent permitted by law, CGM Sports is not liable for damages, injuries, or losses arising from use of the Services. The platform does not replace professional coaching, training, or clinical decision-making."
        }
    ]
};

/**
 * Privacy Policy content
 */
export const privacyPolicy = {
    title: "CGM Sports Privacy Policy",
    lastUpdated: "Effective Date: January 1, 2025",
    sections: [
        {
            heading: "1. Introduction",
            content: "CGM Sports, Inc. (\"CGM Sports,\" \"we,\" \"us,\" or \"our\") is committed to protecting the privacy of individuals whose information we collect and process. This Privacy Policy describes how we collect, use, disclose, and safeguard information in connection with our performance analytics platform (the \"Services\")."
        },
        {
            heading: "2. Information We Collect",
            subsections: [
                {
                    subheading: "2.1 Information Provided by Organizations",
                    content: "We collect information provided by institutional clients (e.g., sports teams, athletic departments, performance facilities, PT clinics) including: athlete/client names, contact information, performance metrics, wearable device data, training load data, wellness questionnaire responses, and other performance-related information."
                },
                {
                    subheading: "2.2 Information Collected Automatically",
                    content: "We automatically collect certain information when you use our Services, including: device information, usage data, log data, and cookies and similar technologies."
                },
                {
                    subheading: "2.3 Information from Third-Party Sources",
                    content: "We may receive information from third-party wearable devices and fitness platforms that you or your organization authorize us to access (e.g., WHOOP, Garmin, Oura, Apple Watch)."
                }
            ]
        },
        {
            heading: "3. How We Use Information",
            content: "We use the information we collect to: provide and maintain the Services, analyze performance trends and generate insights, communicate with users and organizations, improve and develop new features, ensure security and prevent fraud, and comply with legal obligations."
        },
        {
            heading: "4. How We Share Information",
            content: "We do not sell personal information. We may share information in the following circumstances: with your organization (institutional clients have access to data for their athletes/clients), with service providers (trusted third parties who assist in operating our Services), for legal compliance (when required by law or to protect rights and safety), and in business transfers (in connection with mergers, acquisitions, or asset sales)."
        },
        {
            heading: "5. Data Security",
            content: "We implement administrative, technical, and physical safeguards designed to protect your information, including: encryption of data in transit and at rest, access controls and authentication, regular security assessments, and compliance with applicable privacy frameworks (FERPA, HIPAA where applicable)."
        },
        {
            heading: "6. Data Retention",
            content: "We retain information for as long as necessary to provide the Services and fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. When data is no longer needed, we securely delete or anonymize it."
        },
        {
            heading: "7. Your Rights and Choices",
            content: "Depending on your location and applicable law, you may have certain rights regarding your information, including: access and portability, correction and deletion, objection and restriction, and withdrawal of consent. To exercise these rights, please contact us at privacy@cgmsports.com."
        },
        {
            heading: "8. Children's Privacy",
            content: "Our Services are provided to organizations and are not directed to individuals under 13. We do not knowingly collect personal information from children under 13 without parental consent or institutional authorization."
        },
        {
            heading: "9. International Data Transfers",
            content: "If you are located outside the United States, please be aware that information we collect may be transferred to and processed in the United States. We implement appropriate safeguards for international data transfers."
        },
        {
            heading: "10. Changes to This Privacy Policy",
            content: "We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy on our website and updating the \"Effective Date\" above."
        },
        {
            heading: "11. Contact Us",
            content: "If you have questions or concerns about this Privacy Policy or our privacy practices, please contact us at: CGM Sports, Inc., Email: privacy@cgmsports.com, Address: [Your Business Address]"
        }
    ]
};

/**
 * Generate random offsets for letter animation
 */
export const generateLetterOffsets = (text: string, xRange = 100, yRange = 200) => {
    return text.split('').map(() => ({
        x: (Math.random() - 0.5) * xRange,
        y: (Math.random() - 0.5) * yRange
    }));
};

/**
 * Render animated text with letter scramble effect that keeps words together
 */
export interface AnimatedTextProps {
    text: string;
    offsets: { x: number; y: number }[];
    animated: boolean;
    className?: string;
    style?: React.CSSProperties;
}

